import {
  buildDetailPath,
  extractProductIdFromHref,
} from '@/components/modules/products/utils/productHelpers'
import {
  openCategoryDevicePreview,
  openProductContentPreview,
  productContentPreview,
} from '@/components/shared/utils/productContentPreview'
import { isCopiedAbsoluteHref, isExternalHttpHref } from '@/components/shared/utils/copiedAbsoluteHref'
import { requestExternalLinkConfirm } from '@/components/shared/utils/externalLinkConfirm'

export { isCopiedAbsoluteHref } from '@/components/shared/utils/copiedAbsoluteHref'

function isModifiedClick(event) {
  return Boolean(
    event?.metaKey
    || event?.ctrlKey
    || event?.shiftKey
    || event?.altKey
    || event?.button === 1
  )
}

function isCopiedAbsoluteContentLink(event) {
  const node = event?.target
  if (!node?.closest) return false
  const link = node.closest('a[href]')
  if (!link) return false
  if (link.closest('.rich-body-related-links, .hotspot-device-card, .rich-body-product-card, .rich-body-device-link-card, .rich-body-category-portal-card')) return false
  return isCopiedAbsoluteHref(link.getAttribute('href') || '', link.getAttribute('data-link-type') || '')
}

function extractCategoryIdFromHref(href) {
  const match = String(href || '').match(/\/products\/category\/([^/?#]+)/)
  return match ? decodeURIComponent(match[1]) : ''
}

function resolvePreviewTarget(event) {
  const node = event?.target
  if (!node?.closest) return null
  if (node.closest('[data-cert-nav]')) return null

  const link = node.closest('a[href*="/products/detail/"], a[href*="/products/category/"]')
  const card = node.closest(
    '.rich-body-product-card[data-href], .rich-body-product-card[data-product-id], .rich-body-product-card[data-category-id], .rich-body-device-link-card[data-href], .rich-body-device-link-card[data-product-id], .rich-body-device-link-card[data-category-id], .rich-body-category-portal-card[data-href], .rich-body-category-portal-card[data-category-id]',
  )
  const el = link || card
  if (!el) return null

  const href = (link && (link.getAttribute('href') || ''))
    || card?.getAttribute('data-href')
    || card?.getAttribute('href')
    || ''
  const linkType = el.getAttribute('data-link-type')
    || card?.getAttribute('data-link-type')
    || ''
  const dataProductId = el.getAttribute('data-product-id') || card?.getAttribute('data-product-id') || ''
  const dataCategoryId = el.getAttribute('data-category-id') || card?.getAttribute('data-category-id') || ''
  const categoryId = dataCategoryId || extractCategoryIdFromHref(href)
  const productId = dataProductId || extractProductIdFromHref(href)

  if (linkType === 'product' || (productId && linkType !== 'product-category')) {
    if (productId) return { productId, categoryId: '' }
  }
  if (linkType === 'product-category' || categoryId) {
    if (categoryId) return { categoryId, productId: '' }
  }
  return null
}

/** 校验站内回跳路径，防止开放重定向 */
export function sanitizePortalFromPath(from) {
  const value = String(from || '').trim()
  if (!value) return ''
  if (!value.startsWith('/') || value.startsWith('//')) return ''
  if (/^https?:/i.test(value)) return ''
  return value
}

/** 当前页是否为可记录来源的内容页（非首页/产品指南/搜索） */
export function isPortalContentPagePath(path) {
  const normalized = String(path || '').split('?')[0].trim() || '/'
  if (normalized === '/') return false
  if (normalized === '/search') return false
  if (normalized === '/products' || normalized.startsWith('/products/')) return false
  return true
}

/** 解析顶部返回链接：优先 query.from，否则 fallback */
export function resolvePortalBackLink(from, fallback = '/products') {
  const sanitized = sanitizePortalFromPath(from)
  if (sanitized) return sanitized
  return fallback || '/products'
}

/** 从内容页跳转产品时附带 from 查询参数 */
export function buildProductNavigationQuery(fromFullPath) {
  if (!isPortalContentPagePath(fromFullPath)) return {}
  const from = sanitizePortalFromPath(fromFullPath)
  return from ? { from } : {}
}

/** 构建设备详情路由（保留已有 from） */
export function buildProductDetailRoute(productId, fromFullPath = '', existingQuery = {}) {
  const query = { ...existingQuery }
  const preservedFrom = sanitizePortalFromPath(query.from)
  let nextFrom = preservedFrom
  if (!nextFrom && isPortalContentPagePath(fromFullPath)) {
    nextFrom = sanitizePortalFromPath(fromFullPath)
  }
  if (nextFrom) query.from = nextFrom
  else delete query.from
  return {
    path: buildDetailPath(productId),
    query,
  }
}

/** 构建产品分类路由（保留已有 from） */
export function buildProductCategoryRoute(categoryId, fromFullPath = '', existingQuery = {}) {
  const query = { ...existingQuery }
  const preservedFrom = sanitizePortalFromPath(query.from)
  let nextFrom = preservedFrom
  if (!nextFrom && isPortalContentPagePath(fromFullPath)) {
    nextFrom = sanitizePortalFromPath(fromFullPath)
  }
  if (nextFrom) query.from = nextFrom
  else delete query.from
  return {
    path: `/products/category/${categoryId}`,
    query,
  }
}

/** 侧栏/卡片链接：若当前路由带 from 则继续传递 */
export function buildProductDetailLink(productId, route) {
  const from = sanitizePortalFromPath(route?.query?.from)
  if (from) {
    return buildProductDetailRoute(productId, '', route.query)
  }
  return buildDetailPath(productId)
}

/** 设备详情页返回：from 有效且非当前详情页时使用 */
export function resolveProductDetailBackLink(route, fallback) {
  const from = sanitizePortalFromPath(route?.query?.from)
  const currentPath = route?.path || ''
  if (from && from.split('?')[0] !== currentPath && !from.split('?')[0].startsWith('/products/detail/')) {
    return from
  }
  return fallback
}

/** 拦截正文内设备链接点击：产品/分类弹出内容小窗；外站粘贴网址弹出确认后新窗口打开 */
export function handlePortalProductDeviceLinkClick(event) {
  if (isCopiedAbsoluteContentLink(event)) {
    const link = event.target?.closest?.('a[href]')
    const href = link?.getAttribute('href') || ''
    if (!isExternalHttpHref(href)) return false
    event.preventDefault()
    event.stopPropagation()
    requestExternalLinkConfirm(href)
    return true
  }

  if (isModifiedClick(event)) return false

  const target = resolvePreviewTarget(event)
  if (!target) return false

  event.preventDefault()
  event.stopPropagation()

  if (target.categoryId) {
    openCategoryDevicePreview(target.categoryId)
    return true
  }

  if (target.productId) {
    openProductContentPreview(target.productId, {
      fromCategory: Boolean(productContentPreview.visible && productContentPreview.categoryId),
    })
    return true
  }

  return false
}

/** 为 v-csp-html 容器绑定设备链接点击拦截 */
export function bindPortalProductDeviceLinks(el, _instance) {
  if (!el || el.__portalProductLinkBound) return
  el.__portalProductLinkBound = true

  const onClick = (event) => {
    handlePortalProductDeviceLinkClick(event)
  }
  el.addEventListener('click', onClick)
  el.addEventListener('auxclick', onClick)
}
